/**
 * Your CenoX account — the identity, not any one workspace.
 *
 * Lives in the platform shell rather than Business settings, because an
 * account exists whether or not it belongs to a business, and somebody who
 * only ever used CenoX AI must be able to find this page.
 *
 * Deleting the account is typed, not clicked: DELETE or the account's own
 * email. The server is the authority on whether it may happen — a sole owner
 * is refused, with the businesses named — and this page shows that answer
 * rather than guessing at it.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { CenoxShell } from '../../layouts/CenoxShell';
import { Button, Input } from '../../components/ui';

export function AccountPage(): ReactNode {
  const { user, memberships, logout } = useAuth();
  const navigate = useNavigate();

  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [blockedBy, setBlockedBy] = useState<Array<{ id: string; name: string }>>([]);
  const [deleting, setDeleting] = useState(false);

  const typed = confirmation.trim();
  const confirmed =
    typed === 'DELETE' ||
    (user !== null && typed.toLowerCase() === user.email.trim().toLowerCase());

  const finish = async (): Promise<void> => {
    // The server has already ended every session; this clears the browser's
    // copy so nothing on screen pretends otherwise.
    try {
      await logout();
    } finally {
      navigate('/login?deleted=1', { replace: true });
    }
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!confirmed) return;
    setDeleting(true);
    setError(null);
    setBlockedBy([]);
    try {
      await api.post('/auth/delete-account', { confirmation: typed });
      await finish();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'account_not_found') {
        // Already deleted — a retry after the first one went through.
        await finish();
        return;
      }
      if (caught instanceof ApiError && caught.code === 'sole_owner') {
        const names = caught.fields?.businesses ?? [];
        const ids = caught.fields?.businessIds ?? [];
        setBlockedBy(names.map((name, i) => ({ id: ids[i] ?? '', name })));
      }
      setError(caught instanceof ApiError ? caught.message : 'Could not delete your account.');
      setDeleting(false);
    }
  };

  return (
    <CenoxShell>
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">Your account</h1>
          <p className="mt-1 text-sm text-slate-500">
            One CenoX account for every product and every workspace you belong to.
          </p>
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <dl className="grid gap-3 text-sm sm:grid-cols-[8rem_1fr]">
            <dt className="text-slate-500">Name</dt>
            <dd className="text-slate-900">{user?.fullName}</dd>
            <dt className="text-slate-500">Email</dt>
            <dd className="break-all text-slate-900">{user?.email}</dd>
            <dt className="text-slate-500">Workspaces</dt>
            <dd className="text-slate-900">
              {memberships.length === 0
                ? 'None yet'
                : memberships.map((m) => `${m.businessName} (${m.role})`).join(', ')}
            </dd>
          </dl>
        </section>

        <section className="rounded-2xl border border-red-200 bg-white p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-red-800">
            <AlertTriangle className="size-4" aria-hidden />
            Delete your account
          </h2>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
            <li>You are signed out everywhere, immediately.</li>
            <li>You leave every workspace you belong to. Their records stay with them.</li>
            <li>Your name, email and phone are removed from your account. This cannot be undone.</li>
            <li>
              If you are the only owner of a business, transfer ownership to a member or delete the
              business first.
            </li>
          </ul>

          <form onSubmit={submit} className="mt-4 space-y-3" noValidate>
            <Input
              label="Type DELETE or your email address to confirm"
              name="confirmation"
              autoComplete="off"
              value={confirmation}
              onChange={(e) => {
                setConfirmation(e.target.value);
                setError(null);
              }}
            />

            {error && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                <p className="text-sm text-red-800">{error}</p>
                {blockedBy.length > 0 && (
                  <ul className="mt-2 space-y-2">
                    {blockedBy.map((b) => (
                      <li
                        key={b.id || b.name}
                        className="flex flex-wrap items-center gap-2 rounded-lg bg-white px-3 py-2"
                      >
                        <span className="mr-auto text-sm font-medium text-slate-900">{b.name}</span>
                        {b.id && (
                          <>
                            <Link
                              to={`/settings?business=${b.id}&tab=team#transfer`}
                              className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              Transfer ownership
                            </Link>
                            <Link
                              to={`/settings?business=${b.id}&tab=business#delete`}
                              className="rounded-lg border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                            >
                              Delete business
                            </Link>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <Button type="submit" variant="danger" loading={deleting} disabled={!confirmed}>
              Delete my account
            </Button>
          </form>
        </section>
      </div>
    </CenoxShell>
  );
}
